import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getReportEmailDraft, previewReportEmail, sendReportEmail } from '../services/api';
import useDialogFocus from '../hooks/useDialogFocus';
import { apiErrorMessage } from '../utils/apiError';
import AutoGrowTextarea from './AutoGrowTextarea';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function money(value, currency, lang) {
  try {
    return new Intl.NumberFormat(lang === 'en' ? 'en-GB' : 'fr-BE', { style: 'currency', currency: currency || 'EUR' })
      .format(Number(value));
  } catch {
    return `${value} ${currency || ''}`.trim();
  }
}

const parseRecipients = (value) => value
  .split(/[\s,;]+/)
  .map((v) => v.trim())
  .filter(Boolean);

/**
 * Send the report to its client: the PDF attached and the link to read it
 * online. Everything is prefilled and editable; "Preview" shows the e-mail
 * exactly as the server will send it.
 *
 * Given a `draft` (the share panel already has it) it opens straight on the
 * form. Without one — from the reports list or the wizard's top bar — it
 * fetches it first, so every entry point shows the same dialog.
 */
/**
 * The notice after a sending: who got it, and how the PDF went when it was
 * too heavy for the mail servers (lighter photos, or a download link).
 */
export function sentNotice(t, sent) {
  const base = t('sendReport.sent', { list: (sent.recipients || []).join(', '), interpolation: { escapeValue: false } });
  if (sent.pdf_delivery === 'compressed') return `${base} ${t('sendReport.pdfCompressed')}`;
  if (sent.pdf_delivery === 'link') return `${base} ${t('sendReport.pdfByLink')}`;
  return base;
}

export default function SendReportDialog({ reportId, draft: given, onSent, onClose }) {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(null);
  const [failure, setFailure] = useState('');
  const draft = given || loaded;

  useEffect(() => {
    if (given) return undefined;
    let alive = true;
    getReportEmailDraft(reportId)
      .then((d) => { if (alive) setLoaded(d); })
      .catch((err) => { if (alive) setFailure(apiErrorMessage(err, t)); });
    return () => { alive = false; };
  }, [given, reportId, t]);

  // The form only mounts once the draft exists: its fields start from it.
  if (draft?.enabled) {
    return <SendForm reportId={reportId} draft={draft} onSent={onSent} onClose={onClose} />;
  }
  return (
    <StatusDialog onClose={onClose} failure={failure} pending={!draft && !failure}>
      {draft && !draft.enabled && <p>{t('sendReport.notConfigured')}</p>}
    </StatusDialog>
  );
}

// Loading / error / "not configured": a small dialog in the same shell, so
// the screen does not jump when the form replaces it.
function StatusDialog({ onClose, failure, pending, children }) {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef, { onEscape: onClose });
  return (
    <div className="confirm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={dialogRef} tabIndex={-1} className="confirm-dialog send-dialog" role="dialog" aria-modal="true" aria-labelledby="send-title">
        <h2 id="send-title" className="confirm-title">{t('sendReport.title')}</h2>
        {pending && <p role="status">{t('auth.loading')}</p>}
        {failure && <div className="ai-error" role="alert">{failure}</div>}
        {children}
        <div className="confirm-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('sendReport.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SendForm({ reportId, draft, onSent, onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const invoice = draft.invoice || null;
  const [to, setTo] = useState(draft.suggestions[0]?.email || '');
  const [subject, setSubject] = useState(draft.subject);
  const [message, setMessage] = useState(draft.message);
  // What goes with the message — all three by default.
  const [attachPdf, setAttachPdf] = useState(true);
  const [includeLink, setIncludeLink] = useState(true);
  const [sendInvoice, setSendInvoice] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toRef = useRef(null);

  const dialogRef = useRef(null);
  useDialogFocus(dialogRef, { initialRef: toRef, onEscape: () => { if (!busy) onClose(); } });

  const recipients = parseRecipients(to);
  const invalid = recipients.filter((r) => !EMAIL_RE.test(r));

  const addSuggestion = (email) => {
    if (recipients.some((r) => r.toLowerCase() === email.toLowerCase())) return;
    setTo([...recipients, email].join(', '));
  };

  const showPreview = async () => {
    setError('');
    setBusy(true);
    try {
      setPreview(await previewReportEmail(reportId, {
        subject, message, attach_pdf: attachPdf, include_link: includeLink, send_invoice: sendInvoice,
      }));
    } catch (err) {
      setError(apiErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    setError('');
    if (!recipients.length) { setError(t('sendReport.noRecipient')); return; }
    if (invalid.length) { setError(t('sendReport.badRecipient', { list: invalid.join(', '), interpolation: { escapeValue: false } })); return; }
    if (recipients.length > 5) { setError(t('sendReport.tooMany')); return; }
    if (!attachPdf && !includeLink && !sendInvoice) { setError(t('sendReport.nothingChosen')); return; }
    if (sendInvoice && !invoice) { setError(t('sendReport.invoiceMissingError')); return; }
    if (sendInvoice && !invoice.ready) { setError(t('sendReport.invoiceNotReadyError')); return; }
    setBusy(true);
    try {
      const sent = await sendReportEmail(reportId, {
        to: recipients, subject, message,
        attach_pdf: attachPdf, include_link: includeLink, send_invoice: sendInvoice,
      });
      onSent(sent);
    } catch (err) {
      setError(apiErrorMessage(err, t));
      setBusy(false);
    }
  };

  return (
    <div className="confirm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <form
        ref={dialogRef}
        tabIndex={-1}
        className="confirm-dialog send-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-title"
        onSubmit={send}
      >
        <h2 id="send-title" className="confirm-title">{t('sendReport.title')}</h2>
        <p className="send-dialog-intro">
          {t('sendReport.intro')}
          {draft.reply_to && <> {t('sendReport.replyTo', { email: draft.reply_to })}</>}
        </p>

        <fieldset className="send-contents">
          <legend>{t('sendReport.contents')}</legend>
          <label className="send-check">
            <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />
            {t('sendReport.attachPdf')}
          </label>
          <label className="send-check">
            <input type="checkbox" checked={includeLink} onChange={(e) => setIncludeLink(e.target.checked)} />
            <span>
              {t('sendReport.includeLink')}
              <span className="send-check-hint">{t('sendReport.includeLinkHint')}</span>
            </span>
          </label>
          <label className="send-check">
            <input type="checkbox" checked={sendInvoice} onChange={(e) => setSendInvoice(e.target.checked)} />
            <span>
              {t('sendReport.sendInvoice')}
              {invoice?.ready && (
                <span className="send-check-hint">
                  {t('sendReport.invoiceReady', {
                    number: invoice.number || '—',
                    total: money(invoice.total, invoice.currency, i18n.language),
                    interpolation: { escapeValue: false },
                  })}
                  {invoice.emailed_at && ` ${t('sendReport.invoiceAlreadySent')}`}
                </span>
              )}
            </span>
          </label>
          {sendInvoice && !invoice && (
            <div className="send-warning" role="status">
              <p>{t('sendReport.invoiceMissing')}</p>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => navigate(`/invoices?invoice_for=${encodeURIComponent(reportId)}`)}
              >
                {t('sendReport.createInvoice')}
              </button>
            </div>
          )}
          {sendInvoice && invoice && !invoice.ready && (
            <div className="send-warning" role="status">
              <p>{t('sendReport.invoiceNotReady')}</p>
            </div>
          )}
        </fieldset>

        <div className="form-group">
          <label htmlFor="send-to">{t('sendReport.to')}</label>
          <input
            id="send-to"
            ref={toRef}
            className="form-control"
            type="text"
            inputMode="email"
            autoComplete="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            aria-describedby="send-to-hint"
          />
          <p id="send-to-hint" className="send-hint">{t('sendReport.toHint')}</p>
          {draft.suggestions.length > 0 && (
            <div className="send-suggestions">
              {draft.suggestions.map((s) => (
                <button
                  key={s.email}
                  type="button"
                  className="send-suggestion"
                  onClick={() => addSuggestion(s.email)}
                  disabled={recipients.some((r) => r.toLowerCase() === s.email.toLowerCase())}
                >
                  {t(`sendReport.role_${s.role}`)} · {s.name} — {s.email}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="send-subject">{t('sendReport.subject')}</label>
          <input
            id="send-subject"
            className="form-control"
            value={subject}
            maxLength={300}
            onChange={(e) => setSubject(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="send-message">{t('sendReport.message')}</label>
          <AutoGrowTextarea
            id="send-message"
            className="form-control"
            minRows={8}
            value={message}
            maxLength={10000}
            onChange={(e) => setMessage(e.target.value)}
            required
          />
          <p className="send-hint">{t('sendReport.messageHint')}</p>
        </div>

        {preview && (
          <div className="send-preview">
            <div className="send-preview-bar">
              <span>{t('sendReport.previewOf', { subject, interpolation: { escapeValue: false } })}</span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPreview(null)}>
                {t('sendReport.closePreview')}
              </button>
            </div>
            <iframe title={t('sendReport.preview')} srcDoc={preview} sandbox="" className="send-preview-frame" />
          </div>
        )}

        {error && <div className="ai-error" role="alert">{error}</div>}

        <div className="confirm-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {t('clients.cancel')}
          </button>
          <button type="button" className="btn btn-secondary" onClick={showPreview} disabled={busy}>
            {t('sendReport.preview')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t('sendReport.sending') : t('sendReport.send')}
          </button>
        </div>
      </form>
    </div>
  );
}
